import {
  EWithdrawalEntryPoint,
  EWithdrawalSubmitFailure,
} from '@mahawi/eshop-common/dist/src/types';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { requestFailedMatcher } from '../request-failed/reducer';
import {
  EWithdrawalStep,
  type IWithdrawalDraft,
  type IWithdrawalOrderContext,
  type IWithdrawalState,
  type IWithdrawalSubmitPayload,
  type TWithdrawalSubmitResponsePayload,
} from './types';

const createWithdrawalDraft = (
  order: IWithdrawalOrderContext | null,
): IWithdrawalDraft => ({
  name: order?.name ?? '',
  orderNumber: order?.shortCode ?? '',
  orderEmail: order?.email ?? '',
  confirmationEmail: order?.email ?? '',
  confirmationEmailEdited: false,
  reason: '',
  itemsDescription: '',
  items: {},
});

export const createWithdrawalState = ({
  order,
  entryPoint,
}: {
  order: IWithdrawalOrderContext | null;
  entryPoint: EWithdrawalEntryPoint;
}): IWithdrawalState => ({
  order,
  entryPoint,
  step: EWithdrawalStep.FORM,
  draft: createWithdrawalDraft(order),
  submissionKey: null,
  result: null,
  error: null,
  invalidFields: [],
  isSubmitting: false,
});

const initialState: IWithdrawalState = createWithdrawalState({
  order: null,
  entryPoint: EWithdrawalEntryPoint.STOREFRONT,
});

const withdrawalSlice = createSlice({
  name: 'Withdrawal',
  initialState,
  reducers: {
    withdrawalStart: (
      state,
      action: PayloadAction<{
        order: IWithdrawalOrderContext | null;
        entryPoint: EWithdrawalEntryPoint;
      }>,
    ): void => {
      Object.assign(state, createWithdrawalState(action.payload));
    },
    withdrawalSetDraft: (
      state,
      action: PayloadAction<
        Partial<Omit<IWithdrawalDraft, 'confirmationEmailEdited' | 'items'>>
      >,
    ): void => {
      const { orderEmail, confirmationEmail, ...rest } = action.payload;

      Object.assign(state.draft, rest);

      if (orderEmail !== undefined) {
        state.draft.orderEmail = orderEmail;

        if (!state.draft.confirmationEmailEdited) {
          state.draft.confirmationEmail = orderEmail;
        }
      }

      if (confirmationEmail !== undefined) {
        state.draft.confirmationEmail = confirmationEmail;
        state.draft.confirmationEmailEdited = true;
      }

      state.invalidFields = [];
    },
    withdrawalSetItemCount: (
      state,
      action: PayloadAction<{ productUuid: string; count: number | null }>,
    ): void => {
      const { productUuid, count } = action.payload;

      if (count === null) {
        Reflect.deleteProperty(state.draft.items, productUuid);
        return;
      }

      state.draft.items[productUuid] = count;
    },
    withdrawalGoToReview: {
      reducer: (
        state,
        action: PayloadAction<{ submissionKey: string }>,
      ): void => {
        state.submissionKey ??= action.payload.submissionKey;
        state.step = EWithdrawalStep.REVIEW;
        state.error = null;
        state.invalidFields = [];
      },
      prepare: () => ({ payload: { submissionKey: crypto.randomUUID() } }),
    },
    withdrawalBackToForm: (state): void => {
      state.step = EWithdrawalStep.FORM;
      state.error = null;
    },
    withdrawalSubmit: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<IWithdrawalSubmitPayload>,
    ): void => {
      state.isSubmitting = true;
      state.error = null;
    },
    withdrawalSubmitResponse: (
      state,
      action: PayloadAction<TWithdrawalSubmitResponsePayload>,
    ): void => {
      state.isSubmitting = false;

      if (action.payload.ok) {
        state.step = EWithdrawalStep.DONE;
        state.result = action.payload.withdrawal;
        state.error = null;
        return;
      }

      state.error = action.payload.reason;
      state.invalidFields = action.payload.fields ?? [];

      if (action.payload.reason === EWithdrawalSubmitFailure.INVALID) {
        state.step = EWithdrawalStep.FORM;
      }
    },
    withdrawalReset: (state): void => {
      Object.assign(
        state,
        createWithdrawalState({
          order: state.order,
          entryPoint: state.entryPoint,
        }),
      );
    },
  },
  extraReducers: (builder): void => {
    builder.addMatcher(requestFailedMatcher('Withdrawal'), (state): void => {
      state.isSubmitting = false;
      state.error = EWithdrawalSubmitFailure.ERROR;
    });
  },
});

export const {
  withdrawalBackToForm,
  withdrawalGoToReview,
  withdrawalReset,
  withdrawalSetDraft,
  withdrawalSetItemCount,
  withdrawalStart,
  withdrawalSubmit,
  withdrawalSubmitResponse,
} = withdrawalSlice.actions;

export default withdrawalSlice.reducer;
